import { type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import { ActionKeys } from '~/actions/constants';
import * as labelsSettingsAction from '~/actions/labels-settings.actions';
import { fetchServiceLevelsByAccount } from '~/actions/providers.actions';
import * as userActions from '~/actions/user.actions';
import { LABEL_SETTINGS_UPDATED } from '~/constants/gtm';
import { sendGTMEvent } from '~/services/gtm.service';
import settingsService from '~/services/settings.service';
import {
  labelSettingsFromJSON,
  labelSettingsToJSON,
  type JSONLabelSetting,
} from '~/types/LabelSettings';

function* fetchLabelSettings() {
  try {
    const response: AxiosResponse<JSONLabelSetting> = yield call([
      settingsService,
      settingsService.fetchLabelSettings,
    ]);

    const responsePayload = labelSettingsFromJSON(response.data);

    yield put(labelsSettingsAction.fetchLabelSettingsSuccess(responsePayload));
  } catch (err) {
    // Continue with error.
  }
}

function* saveLabelSettings(action: ReturnType<typeof labelsSettingsAction.submitLabelsForm>) {
  try {
    const formPayload = labelSettingsToJSON(action.payload.formData);

    yield call(sendGTMEvent, { eventName: LABEL_SETTINGS_UPDATED });

    const response: AxiosResponse = yield call(
      [settingsService, settingsService.saveLabelSettings],
      formPayload,
    );

    // Current endpoint in go doesn't support saving default service level
    // Since they don't match between the db and the list the UI its getting from django
    // Need to save it in a different call to /account :/

    if (formPayload.default_outbound_service_level || formPayload.default_return_service_level) {
      const serviceLevelResponse: AxiosResponse = yield call(
        settingsService.saveDefaultServiceLevel,
        formPayload.default_outbound_service_level,
        formPayload.default_return_service_level,
      );

      if (formPayload.default_provider && formPayload.default_outbound_service_level) {
        yield put(
          userActions.updateUserDefaultServiceLevel(
            formPayload.default_provider,
            formPayload.default_outbound_service_level,
          ),
        );
      }

      if (formPayload.default_return_provider && formPayload.default_return_service_level) {
        yield put(
          userActions.updateUserDefaultReturnServiceLevel(
            formPayload.default_return_provider,
            formPayload.default_return_service_level,
          ),
        );
      }

      if (formPayload.default_outbound_service_level || formPayload.default_return_service_level) {
        // update the Service Levels by Account in the store to reflect which service levels are marked "is_used"
        yield call(fetchServiceLevelsByAccount);
      }

      response.data.default_provider = serviceLevelResponse.data.default_provider;
      response.data.default_service_level = serviceLevelResponse.data.default_service_level;
    }

    const responsePayload = labelSettingsFromJSON(response.data);

    yield put(labelsSettingsAction.didSaveLabel(responsePayload));
    yield put(userActions.refreshUser());
  } catch (err) {
    yield put(userActions.refreshUser());
  }
}

export default [
  takeLatest(ActionKeys.SAVE_LABEL_SETTINGS, saveLabelSettings),
  takeLatest(ActionKeys.FETCH_LABEL_SETTINGS, fetchLabelSettings),
];
